import React, { useState, useEffect } from "react";
import { Sun, Send, Search, CheckSquare, Square } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Admin: pick any number of customers and push them today's chart-based horoscope.
export default function HoroscopeSender() {
  const [recipients, setRecipients] = useState([]);
  const [day, setDay] = useState("");
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [onlyReady, setOnlyReady] = useState(true);
  const [lang, setLang] = useState("en");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/horoscope/recipients`, { withCredentials: true });
      setRecipients(data.recipients || []);
      setDay(data.day || "");
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Failed to load customers." });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const visible = recipients.filter(r => {
    if (onlyReady && !(r.has_kundli && r.has_device)) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allVisibleSelected = visible.length > 0 && visible.every(r => selected.includes(r.id));
  const toggleAll = () => setSelected(allVisibleSelected ? [] : visible.map(r => r.id));

  const send = async () => {
    if (!selected.length) { setResult({ error: "Select at least one customer." }); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/horoscope/send`,
        { user_ids: selected, lang }, { withCredentials: true, timeout: 300000 });
      setResult({ ok: true, ...data });
      load();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Failed to send horoscope pushes." });
    } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-horoscope-panel">
      <div style={{ marginBottom: "18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Sun size={20} style={{ color: "#FFD166" }} />
          <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166" }}>Daily Horoscope Push</h2>
        </div>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: "4px" }}>
          Horoscopes for {day || "today"} are generated from each customer&apos;s own Kundli. Select customers and send.
        </p>
      </div>

      <div className="glass-card" style={{ padding: "18px", marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "14px" }}>
          <div style={{ position: "relative", flex: "1 1 220px" }}>
            <Search size={13} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.35)" }} />
            <input data-testid="horo-search" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search name or email" className="cosmic-input" style={{ paddingLeft: "30px", fontSize: "0.84rem" }} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", cursor: "pointer" }}>
            <input data-testid="horo-only-ready" type="checkbox" checked={onlyReady} onChange={e => { setOnlyReady(e.target.checked); setSelected([]); }} />
            Only customers with a Kundli + app device
          </label>
          <select data-testid="horo-lang-select" value={lang} onChange={e => setLang(e.target.value)}
            className="cosmic-input" style={{ width: "auto", fontSize: "0.82rem" }}>
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", flexWrap: "wrap", gap: "8px" }}>
          <button data-testid="horo-select-all" onClick={toggleAll}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#FFD166", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "6px", padding: 0 }}>
            {allVisibleSelected ? <CheckSquare size={14} /> : <Square size={14} />}
            {allVisibleSelected ? "Clear selection" : `Select all (${visible.length})`}
          </button>
          <span data-testid="horo-selected-count" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem" }}>
            {selected.length} selected
          </span>
        </div>

        <div data-testid="horo-recipient-list" style={{ maxHeight: "340px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
          {loading ? (
            <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>Loading customers…</p>
          ) : visible.length === 0 ? (
            <p data-testid="horo-empty" style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.85rem" }}>No matching customers.</p>
          ) : visible.map((r, i) => (
            <label key={r.id} data-testid={`horo-recipient-${i}`}
              style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 11px", borderRadius: "9px", cursor: "pointer",
                background: selected.includes(r.id) ? "rgba(255,209,102,0.09)" : "rgba(255,255,255,0.025)",
                border: `1px solid ${selected.includes(r.id) ? "rgba(255,209,102,0.28)" : "rgba(255,255,255,0.05)"}` }}>
              <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: "white", fontSize: "0.86rem", fontWeight: 600 }}>{r.name || "—"}</div>
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.74rem" }}>{r.email}</div>
              </div>
              <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
                {[["Kundli", r.has_kundli, "#FFD166"], ["Device", r.has_device, "#67e8f9"], ["Ready", r.horoscope_ready, "#86efac"]].map(([label, on, color]) => (
                  <span key={label} style={{ fontSize: "0.64rem", padding: "2px 7px", borderRadius: "50px",
                    background: on ? `${color}1f` : "rgba(255,255,255,0.04)",
                    color: on ? color : "rgba(255,255,255,0.28)", border: `1px solid ${on ? color + "44" : "transparent"}` }}>
                    {label}
                  </span>
                ))}
              </div>
            </label>
          ))}
        </div>
      </div>

      <button data-testid="horo-send-btn" onClick={send} disabled={sending || !selected.length}
        className="btn-gold" style={{ display: "inline-flex", alignItems: "center", gap: "7px", opacity: (sending || !selected.length) ? 0.55 : 1 }}>
        <Send size={14} /> {sending ? "Sending…" : `Send horoscope to ${selected.length || ""} customer${selected.length === 1 ? "" : "s"}`}
      </button>

      {result && (
        <div data-testid="horo-result" style={{ marginTop: "14px", padding: "12px 14px", borderRadius: "10px",
          background: result.error ? "rgba(239,68,68,0.1)" : "rgba(134,239,172,0.1)",
          border: `1px solid ${result.error ? "rgba(239,68,68,0.25)" : "rgba(134,239,172,0.25)"}`,
          color: result.error ? "#fca5a5" : "#86efac", fontSize: "0.84rem" }}>
          {result.error ? result.error : (
            <>
              Sent to {result.sent} of {result.requested} customer(s).
              {result.skipped?.length > 0 && (
                <div style={{ marginTop: "6px", color: "rgba(255,255,255,0.5)", fontSize: "0.78rem" }}>
                  Skipped: {result.skipped.map(s => s.reason).join(", ")}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
